import { useQuery } from "@apollo/client";
import { Box, LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import React from "react";
import { REPORT_RETENTION } from "./dashboardReports.queries";
import { formatCount, formatPercent, formatPeriod, Granularity } from "./reportFormat";
import { rangeVariables, ReportFilterState } from "./ReportFilters";
import { ResultView } from "./reportParams";
import { BAR, ReportError, Section, StatTile, TileRow } from "./reportParts";
import { RetentionPdfButton } from "./pdf/exportButtons";

interface RetentionDay {
  eligible: number;
  retained: number;
  rate: number | null;
}

const DAYS = [
  { key: "day1", label: "Day 1" },
  { key: "day7", label: "Day 7" },
  { key: "day30", label: "Day 30" },
] as const;

/**
 * The reference palette's sequential blue, light to dark. Step 450 and darker
 * carry white text, lighter steps the primary text colour. Step 400 is left out:
 * neither text colour reaches 4.5:1 on it.
 */
const RAMP = ["#cde2fb", "#9ec5f4", "#6da7ec", "#2a78d6", "#1c5cab", "#104281"];
const FIRST_DARK_STEP = 3;

export function shade(rate: number, highest: number): { background: string; color?: string } {
  const step = Math.min(RAMP.length - 1, Math.floor((highest > 0 ? rate / highest : 0) * (RAMP.length - 1)));
  return { background: RAMP[step], color: step >= FIRST_DARK_STEP ? "#ffffff" : undefined };
}

const caption = (day: RetentionDay) =>
  day.eligible ? `${formatCount(day.retained)} of ${formatCount(day.eligible)} members` : "No one's day has ended yet";

/**
 * One small chart per day, oldest group first, all three on one scale from zero
 * so they can be compared across. A group whose day is not over yet has no bar
 * rather than an empty one, which would read as nobody coming back.
 */
const RetentionCharts: React.FC<{ cohorts: any[]; cohortSize: Granularity; highest: number }> = ({ cohorts, cohortSize, highest }) => (
  <Box sx={{ display: "grid", gap: 2.5, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
    {DAYS.map(({ key, label }) => (
      <Box key={key} sx={{ minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ fontVariantNumeric: "tabular-nums" }}>
          {highest > 0 ? `Top of the chart: ${formatPercent(highest)}` : "Nobody came back in this range"}
        </Typography>
        <Box
          role="img"
          aria-label={`${label} retention by when members registered. The table view lists the same numbers.`}
          sx={{ display: "flex", alignItems: "flex-end", gap: "2px", height: 140, borderBottom: 1, borderColor: "divider" }}
        >
          {cohorts.map((cohort) => {
            const day: RetentionDay = cohort[key];
            const period = formatPeriod(cohort.cohort, cohortSize);
            const reading = day.rate === null ? "Not over yet" : `${formatPercent(day.rate)}, ${caption(day)}`;
            return (
              <Tooltip key={cohort.cohort} title={`${period}: ${reading}`} placement="top">
                {/* The whole column answers the hover, so a short bar is as easy to reach as a tall one. */}
                <Box sx={{ flex: "1 1 0", minWidth: 0, height: "100%", display: "flex", alignItems: "flex-end", justifyContent: "center" }}>
                  {day.rate !== null && (
                    <Box
                      sx={{
                        width: "100%",
                        maxWidth: 36,
                        // A sliver at 0%, so it is told apart from the gap of a day not over yet.
                        height: `${highest > 0 ? Math.max(1, (day.rate / highest) * 100) : 1}%`,
                        bgcolor: BAR,
                        borderRadius: "4px 4px 0 0",
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
        {cohorts.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, color: "text.secondary", fontSize: 12 }}>
            <span>{formatPeriod(cohorts[0].cohort, cohortSize)}</span>
            {cohorts.length > 1 && <span>{formatPeriod(cohorts[cohorts.length - 1].cohort, cohortSize)}</span>}
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

export const RetentionTab: React.FC<{ filters: ReportFilterState; cohortSize: Granularity; view: ResultView }> = ({
  filters,
  cohortSize,
  view,
}) => {
  const { data, loading, error } = useQuery(REPORT_RETENTION, {
    variables: { range: rangeVariables(filters, cohortSize) },
    fetchPolicy: "cache-and-network",
  });
  const report = data?.reportRetention;
  const highest = Math.max(
    0,
    ...((report?.cohorts ?? []) as any[]).flatMap((cohort) => DAYS.map(({ key }) => cohort[key].rate ?? 0)),
  );

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <RetentionPdfButton report={report && !loading ? report : null} filters={filters} cohortSize={cohortSize} />
      </Box>
      <ReportError error={error} />
      {loading && <LinearProgress />}
      {report && (
        <>
          <Section
            title="Members who came back"
            note={
              <>
                Of the {formatCount(report.overall.members)} members who registered in this range, how many opened the app on
                their first day after registering, their seventh and their thirtieth. A member only counts once that day is over
                for them, so recent signups are left out rather than counted as gone.
              </>
            }
          >
            <TileRow>
              {DAYS.map(({ key, label }) => (
                <StatTile key={key} label={`${label} retention`} value={formatPercent(report.overall[key].rate)} caption={caption(report.overall[key])} />
              ))}
            </TileRow>
          </Section>

          <Section
            title="By when members registered"
            note={
              view === "chart"
                ? "Each bar is the members who registered that week or month, oldest first, on one scale. Hover a bar for its numbers. A gap means the day is not over yet for anyone in that group."
                : "Each row is the members who registered that week or month. Darker cells kept more of their members; a dash means the day is not over yet for anyone in that group."
            }
          >
            {view === "chart" ? (
              <RetentionCharts cohorts={report.cohorts} cohortSize={cohortSize} highest={highest} />
            ) : (
              <Box sx={{ overflow: "auto", maxHeight: 520 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Registered</TableCell>
                      <TableCell align="right">Members</TableCell>
                      {DAYS.map(({ key, label }) => (
                        <TableCell key={key} align="center">
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {report.cohorts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>No one registered in this range.</TableCell>
                      </TableRow>
                    )}
                    {[...report.cohorts].reverse().map((cohort: any) => (
                      <TableRow key={cohort.cohort}>
                        <TableCell>{formatPeriod(cohort.cohort, cohortSize)}</TableCell>
                        <TableCell align="right">{formatCount(cohort.members)}</TableCell>
                        {DAYS.map(({ key }) => {
                          const day: RetentionDay = cohort[key];
                          const style = day.rate === null ? undefined : shade(day.rate, highest);
                          return (
                            <TableCell
                              key={key}
                              align="center"
                              title={day.eligible ? caption(day) : "Not over yet for anyone in this group"}
                              sx={{
                                bgcolor: style?.background,
                                color: style?.color,
                                borderBottom: "2px solid",
                                borderBottomColor: "background.paper",
                                fontVariantNumeric: "tabular-nums",
                                minWidth: 110,
                              }}
                            >
                              {formatPercent(day.rate)}
                              {day.eligible > 0 && (
                                <Box component="span" sx={{ opacity: 0.75, fontSize: 12, ml: 0.75 }}>
                                  {day.retained}/{day.eligible}
                                </Box>
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </Section>
        </>
      )}
    </Stack>
  );
};
